import React, { useCallback, useEffect, useRef, useState } from 'react';
import './PortailClient.css';

// ─── Portail client — page PUBLIQUE, ouverte depuis un lien ──────────────────
// Le client arrive par un lien reçu de sa concession, reçoit un code par SMS,
// et dépose ses pièces. Rien d'autre.
//
// ⚠ Conçue pour un TÉLÉPHONE, en une main, par quelqu'un qui ne connaît pas le
// CRM : une pièce = une carte, un bouton, l'appareil photo qui s'ouvre. Pas de
// glisser-déposer (impossible sur mobile), pas de menu, pas de compte.
//
// ⚠ `capture="environment"` ouvre l'appareil photo arrière directement, sans
// passer par la galerie. C'est ce qui fait le « un clic ».

const API = '/api/public/mon-dossier';

const ETATS = {
  attendue: { libelle: 'À fournir', classe: 'attendue' },
  recue:    { libelle: 'Reçue',     classe: 'recue' },
  validee:  { libelle: 'Validée',   classe: 'validee' },
  refusee:  { libelle: 'À refaire', classe: 'refusee' },
};

export default function PortailClient({ token }) {
  const [etape, setEtape] = useState('ouverture'); // ouverture | code | pieces | ferme
  const [entete, setEntete] = useState(null);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(null);        // { canal, destinataire }
  const [code, setCode] = useState('');
  const [session, setSession] = useState('');
  const [pieces, setPieces] = useState([]);
  const [enCours, setEnCours] = useState(null);    // typeId en cours de dépôt
  const refs = useRef({});

  const appel = useCallback(async (chemin, opt = {}) => {
    const r = await fetch(`${API}/${token}${chemin}`, {
      ...opt,
      headers: {
        ...(opt.body ? { 'Content-Type': 'application/json' } : {}),
        ...(session ? { Authorization: `Bearer ${session}` } : {}),
        ...(opt.headers || {}),
      },
    });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(b.error || 'Une erreur est survenue');
    return b;
  }, [token, session]);

  // 1. Ouvrir le lien, puis demander le code aussitôt : le client n'a rien à
  // saisir d'autre que les six chiffres.
  useEffect(() => {
    let actif = true;
    (async () => {
      let e;
      try {
        e = await appel('');           // le lien est-il encore vivant ?
      } catch (err) {
        if (actif) { setErreur(err.message); setEtape('ferme'); }
        return;
      }
      if (!actif) return;
      setEntete(e); setEtape('code');
      // L'envoi du code peut échouer sans que le lien soit en cause : on reste
      // sur l'écran du code, avec le bouton de renvoi.
      try {
        const c = await appel('/code', { method: 'POST' });
        if (actif) setEnvoi(c);
      } catch (err) {
        if (actif) setErreur(err.message);
      }
    })();
    return () => { actif = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const chargerPieces = useCallback(async () => {
    const p = await appel('/pieces');
    setPieces(p.pieces);
  }, [appel]);

  const valider = async () => {
    setErreur('');
    try {
      const r = await appel('/verifier', { method: 'POST', body: JSON.stringify({ code }) });
      setSession(r.session);
      setEtape('pieces');
    } catch (e) { setErreur(e.message); setCode(''); }
  };
  useEffect(() => { if (session) chargerPieces().catch((e) => setErreur(e.message)); }, [session, chargerPieces]);

  const renvoyer = async () => {
    setErreur('');
    try { setEnvoi(await appel('/code', { method: 'POST' })); }
    catch (e) { setErreur(e.message); }
  };

  const deposer = async (typeId, fichier) => {
    if (!fichier) return;
    setEnCours(typeId); setErreur('');
    try {
      const pre = await appel(`/pieces/${typeId}/presign`, {
        method: 'POST', body: JSON.stringify({ filename: fichier.name, contentType: fichier.type }),
      });
      await fetch(pre.uploadUrl, { method: 'PUT', body: fichier, headers: { 'Content-Type': fichier.type } });
      await appel(`/pieces/${typeId}/confirm`, {
        method: 'POST',
        body: JSON.stringify({ key: pre.key, filename: fichier.name, contentType: fichier.type, size: fichier.size }),
      });
      await chargerPieces();
    } catch (e) { setErreur(e.message); }
    finally { setEnCours(null); }
  };

  if (etape === 'ouverture') return <div className="pc"><div className="pc-carte"><p className="pc-attente">Un instant…</p></div></div>;

  if (etape === 'ferme') {
    return (
      <div className="pc">
        <div className="pc-carte">
          <h1>Lien expiré</h1>
          <p className="pc-texte">{erreur}</p>
        </div>
      </div>
    );
  }

  if (etape === 'code') {
    return (
      <div className="pc">
        <div className="pc-carte">
          <h1>Vos documents</h1>
          {entete?.prenom && <p className="pc-texte">Bonjour {entete.prenom},</p>}
          {envoi ? (
            <p className="pc-texte">
              Nous venons de vous envoyer un code {envoi.canal === 'email' ? 'par e-mail' : 'par SMS'}
              {envoi.destinataire ? <> au <b>{envoi.destinataire}</b></> : null}.
            </p>
          ) : (
            <p className="pc-texte">Saisissez le code reçu pour accéder à vos documents.</p>
          )}
          <input className="pc-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            placeholder="— — — — — —" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
          {erreur && <p className="pc-erreur">{erreur}</p>}
          <button className="pc-bouton" disabled={code.length !== 6} onClick={valider}>Continuer</button>
          <button className="pc-lien" onClick={renvoyer}>
            {envoi ? "Je n'ai rien reçu" : 'Recevoir un code'}
          </button>
        </div>
      </div>
    );
  }

  const reste = pieces.filter((p) => p.obligatoire && p.etat !== 'recue' && p.etat !== 'validee').length;
  return (
    <div className="pc">
      <div className="pc-carte large">
        <h1>Vos documents</h1>
        <p className="pc-texte">
          Dossier {entete?.dossier}{entete?.vehicule ? ` · ${entete.vehicule}` : ''}
        </p>
        <p className={`pc-reste${reste === 0 ? ' fini' : ''}`}>
          {reste === 0 ? '✅ Tout est là, merci !' : `${reste} document${reste > 1 ? 's' : ''} encore à fournir`}
        </p>
        {erreur && <p className="pc-erreur">{erreur}</p>}

        <ul className="pc-liste">
          {pieces.map((p) => {
            const e = ETATS[p.etat] || ETATS.attendue;
            const aRefaire = p.etat === 'refusee';
            const fait = p.etat === 'recue' || p.etat === 'validee';
            return (
              <li key={p.typeId} className={`pc-piece ${e.classe}`}>
                <div className="pc-piece-t">
                  <b>{p.nom}</b>
                  {p.obligatoire && !fait && <span className="pc-obli">obligatoire</span>}
                  <span className={`pc-etat ${e.classe}`}>{e.libelle}</span>
                </div>
                {aRefaire && p.motifRefus && <div className="pc-motif">{p.motifRefus}</div>}
                {(!fait || aRefaire) && (
                  <>
                    <button className="pc-bouton petit" disabled={enCours === p.typeId}
                      onClick={() => refs.current[p.typeId]?.click()}>
                      {enCours === p.typeId ? 'Envoi…' : aRefaire ? '📷 Refaire la photo' : '📷 Photographier ou choisir'}
                    </button>
                    <input ref={(el) => { refs.current[p.typeId] = el; }} type="file"
                      accept="image/*,application/pdf" capture="environment" style={{ display: 'none' }}
                      onChange={(e) => { deposer(p.typeId, e.target.files?.[0]); e.target.value = ''; }} />
                  </>
                )}
              </li>
            );
          })}
        </ul>
        <p className="pc-pied">Vos documents sont transmis directement à votre concession.</p>
      </div>
    </div>
  );
}
